import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FacebookShareWrapper, AdviceBox, ShareActionsWrapper } from './Styled';
import Button from 'components/buttons/Button';
import { ErrorAlert } from 'components/Alert';

import { usePostActions } from '../PostActions';
import {
  AcceptPostRequest,
  verifyTokenSocialNetwork
} from 'services/rest/post';

import FacebookPrivacyEs from 'images/icons/facebook_privacy_es.svg';
import FacebookPrivacyEn from 'images/icons/facebook_privacy_en.svg';
import FacebookPrivacyPt from 'images/icons/facebook_privacy_pt.svg';
import { urlFacebook } from 'constants/setup.constants';
import FacebookInstructionsEs from 'images/backgrounds/share_fb_es.gif';
import FacebookInstructionsEn from 'images/backgrounds/share_fb_en.gif';
import FacebookInstructionsPt from 'images/backgrounds/share_fb_pt.gif';
import { addInvalidNetwork } from 'redux/slices/InvalidToken/InvalidTokenSlice';
import { CampaignParams } from 'pages/CampaignDetailPage/CampaignDetailPage';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { selectCompany } from 'redux/slices/Company/CompanySlice';

const privacyImages: Record<string, string> = {
  es: FacebookPrivacyEs,
  en: FacebookPrivacyEn,
  pt: FacebookPrivacyPt
};

const shareInstructions: Record<string, string> = {
  es: FacebookInstructionsEs,
  en: FacebookInstructionsEn,
  pt: FacebookInstructionsPt
};

export interface FacebookShareCardProps {
  postId: number;
  originalContent: string;
  provider: string;
  setShowButton?: any;
  getRankingData?: any;
  setPublicationLoader: any;
}

const FacebookShareCard: React.FC<FacebookShareCardProps> = props => {
  const { campaignId } = useParams<CampaignParams>();
  const { currentUser } = useSelector(selectUser);
  const { company } = useSelector(selectCompany);
  const dispatch = useDispatch();
  const {
    originalContent,
    postId,
    provider,
    setShowButton,
    getRankingData,
    setPublicationLoader
  } = props;
  const { t, i18n } = useTranslation([
    'content_editor',
    'validations',
    'settings',
    'social_network',
    'networks_list'
  ]);

  const { onPublishPost, onErrorPost } = usePostActions();
  const [loaderShare, setLoaderShare] = useState(false);
  const [loaderFinish, setLoaderFinish] = useState(false);
  const [hasShared, setHasShared] = useState(false);
  const [errorAlertVisible, setErrorAlertVisible] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [showTokenError, setShowTokenError] = useState(false);

  const onClickShare = async () => {
    let shareWindow = null;

    try {
      setLoaderShare(true);
      setErrorAlertVisible('');
      setShowTokenError(false);

      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        // Navegadores móviles
        shareWindow = window.open('about:blank', '_blank');
        if (
          !shareWindow ||
          shareWindow.closed ||
          typeof shareWindow.closed === 'undefined'
        ) {
          window.location.href = originalContent;
          return;
        }

        const { data } = await verifyTokenSocialNetwork(
          provider,
          currentUser?.token
        );

        if (data) {
          setTokenValid(true);
          setLoaderShare(false);

          shareWindow.location.href = originalContent;
          setHasShared(true);
        } else {
          if (shareWindow && !shareWindow.closed) {
            shareWindow.close();
          }
        }
      } else {
        // Navegadores de escritorio
        shareWindow = window.open(
          'about:blank',
          'facebook-share-dialog',
          'width=600,height=400,left=100,top=100,resizable=1,scrollbars=1'
        );
        if (
          !shareWindow ||
          shareWindow.closed ||
          typeof shareWindow.closed === 'undefined'
        ) {
          window.location.href = `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            originalContent
          )}&display=popup`;
          return;
        }

        const { data } = await verifyTokenSocialNetwork(
          provider,
          currentUser?.token
        );

        if (data) {
          setTokenValid(true);
          setLoaderShare(false);

          shareWindow.location.href = `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            originalContent
          )}&display=popup&ref=plugin&src=post`;
          setHasShared(true);
        } else {
          if (shareWindow && !shareWindow.closed) {
            shareWindow.close();
          }
        }
      }
    } catch (error: any) {
      setTokenValid(false);
      setLoaderShare(false);

      if (shareWindow && !shareWindow.closed) {
        shareWindow.close();
      }

      const errorMessage = error.message || String(error);
      if (errorMessage.includes('permisos')) {
        dispatch(addInvalidNetwork(provider));
        setShowTokenError(true);
        setErrorAlertVisible(
          t('social_network:token_permissions', {
            invalidToken: provider
          })
        );
      } else {
        setErrorAlertVisible(t('validations:error_facebook_share'));
      }
    }
  };

  const conect = (conect: string) => {
    switch (conect) {
      case 'facebook':
        window.location.href = urlFacebook(
          company?.facebookCredentials.apiKey,
          conect
        );
        break;
    }
  };

  const onPublish = async () => {
    if (!tokenValid) {
      setErrorAlertVisible(t('validations:must_share_first'));
      return;
    }
    setLoaderFinish(true);
    setPublicationLoader(true);
    try {
      const { data, errors } = await AcceptPostRequest(
        Number(campaignId),
        originalContent,
        provider,
        currentUser?.token,
        '',
        false,
        ''
      );
      if (data) {
        onPublishPost(postId, data.data);
        setLoaderFinish(false);
        setPublicationLoader(false);
        setShowButton('published');
        getRankingData();
      }
      if (errors) {
        if (errors.data === 'No se encontró el post, intenta nuevamente') {
          setErrorAlertVisible(t('validations:post_not_found_error'));
        } else {
          onErrorPost(t('settings:error_admin'));
          if (errors && errors.data) {
            onErrorPost(errors.data);
          }
        }
        setLoaderFinish(false);
        setPublicationLoader(false);
        setShowButton('pending');
      }
    } catch (error) {
      setLoaderFinish(false);
      setPublicationLoader(false);
      setErrorAlertVisible(t('validations:error_finishing_share'));
    }
  };

  return (
    <FacebookShareWrapper>
      {errorAlertVisible && !showTokenError && (
        <ErrorAlert
          text={errorAlertVisible}
          allowClose={true}
          onClose={() => setErrorAlertVisible('')}
        />
      )}

      {showTokenError ? (
        <Fragment>
          <p>{`${t('social_network:token_permissions', {
            invalidToken: provider
          })}`}</p>
          <Button
            value={t('networks_list:update')}
            variant="tertiary"
            as="a"
            size="big"
            onClick={() => conect(provider)}
            target="_blank"
          />
        </Fragment>
      ) : (
        <Fragment>
          <h2>{t('facebook_steps')}</h2>
          <AdviceBox>
            <ol>
              <li className="share">
                {t('click_on')}&nbsp;
                <strong>Share</strong>
                {t('share_facebook_content')}
              </li>

              <li>
                <span className="public-img">
                  {t('check_post_is_public')}&nbsp;
                  <img
                    src={privacyImages[i18n.language]}
                    alt="Facebook privacy"
                  />
                  &nbsp;{t('see_gif')}
                </span>
              </li>

              <li>
                <strong>{t('come_back_here')}</strong>
              </li>

              <li className="finish">
                {t('click_on')}&nbsp;
                <strong>{t('finish')}</strong>
                {t('sum_points')}
              </li>
            </ol>
          </AdviceBox>
          <img
            className="fb-share-instructions"
            src={shareInstructions[i18n.language]}
            alt="Facebook share instructions"
          />
        </Fragment>
      )}

      {company?.company_permission.allowFacebook && !showTokenError && (
        <ShareActionsWrapper>
          <Button
            icon="facebook"
            variant="facebook"
            value="Share"
            size="medium"
            onClick={onClickShare}
            loader={loaderShare}
          />
          {hasShared && tokenValid && (
            <Button
              onClick={onPublish}
              className="finish"
              value={t('finish')}
              size="medium"
              loader={loaderFinish}
            />
          )}
        </ShareActionsWrapper>
      )}
    </FacebookShareWrapper>
  );
};

export default FacebookShareCard;
